/**
 * Prefetch utilities for React Query
 * Uses data layer functions and centralized query keys
 */

import { QueryClient } from "@tanstack/react-query";
import { getCurrentUserCompanyId } from "@/lib/userUtils";
import {
  companyKeys,
  bankKeys,
  vendorKeys,
  menuKeys,
  ingredientKeys,
  prepItemKeys,
  integrationKeys,
  queryOptions,
} from "@/data/query-keys";
import {
  getCompanySettings,
  getBankAccountsWithTraining,
  getVendors,
  getMenuItems,
  getIngredients,
  getPrepItems,
  getMenuCategories,
  getIntegrations,
} from "@/data/supabase";

/**
 * Prefetch common data that users are likely to need
 * This runs in the background to improve perceived performance
 * Optimized: Delayed execution to avoid blocking initial render
 */
export const prefetchCommonData = async (queryClient: QueryClient) => {
  // Delay prefetching until after initial render using requestIdleCallback or setTimeout
  const schedulePrefetch = () => {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => executePrefetch(queryClient), { timeout: 2000 });
    } else {
      setTimeout(() => executePrefetch(queryClient), 2000);
    }
  };

  schedulePrefetch();
};

/**
 * Immediate prefetch (no delay) - use when session is already ready
 */
export const prefetchCommonDataImmediate = async (queryClient: QueryClient, companyId?: string) => {
  const id = companyId || await getCurrentUserCompanyId();
  if (!id) return;
  await executePrefetchWithCompanyId(queryClient, id);
};

const executePrefetch = async (queryClient: QueryClient) => {
  try {
    const companyId = await getCurrentUserCompanyId();
    if (!companyId) return;
    await executePrefetchWithCompanyId(queryClient, companyId);
  } catch (error) {
    // Silently fail - prefetching shouldn't block the UI
    console.debug("Prefetch error (non-critical):", error);
  }
};

const executePrefetchWithCompanyId = async (queryClient: QueryClient, companyId: string) => {
  // Prefetch critical data using data layer functions
  await Promise.all([
    // Bank Accounts with training
    queryClient.prefetchQuery({
      queryKey: bankKeys.list(),
      queryFn: () => getBankAccountsWithTraining(companyId),
      staleTime: queryOptions.bankAccounts.staleTime,
    }),

    // Vendors
    queryClient.prefetchQuery({
      queryKey: vendorKeys.list(),
      queryFn: () => getVendors(companyId),
      staleTime: queryOptions.vendors.staleTime,
    }),

    // Company Settings
    queryClient.prefetchQuery({
      queryKey: companyKeys.settings(),
      queryFn: () => getCompanySettings(companyId),
      staleTime: queryOptions.company.staleTime,
    }),

    // Integrations
    queryClient.prefetchQuery({
      queryKey: integrationKeys.list(),
      queryFn: () => getIntegrations(companyId),
      staleTime: queryOptions.integrations.staleTime,
    }),

    // Menu Items
    queryClient.prefetchQuery({
      queryKey: menuKeys.items(),
      queryFn: () => getMenuItems(companyId),
      staleTime: queryOptions.menu.staleTime,
    }),

    // Ingredients
    queryClient.prefetchQuery({
      queryKey: ingredientKeys.list(),
      queryFn: () => getIngredients(companyId),
      staleTime: queryOptions.ingredients.staleTime,
    }),

    // Prep Items
    queryClient.prefetchQuery({
      queryKey: prepItemKeys.list(),
      queryFn: () => getPrepItems(companyId),
      staleTime: queryOptions.prepItems.staleTime,
    }),

    // Menu Categories
    queryClient.prefetchQuery({
      queryKey: menuKeys.categories(),
      queryFn: () => getMenuCategories(companyId),
      staleTime: queryOptions.menu.staleTime,
    }),
  ]);
};

/**
 * Prefetch data for a specific route
 */
export const prefetchRouteData = async (
  queryClient: QueryClient,
  route: string
) => {
  try {
    const companyId = await getCurrentUserCompanyId();
    if (!companyId) return;

    // Route-specific prefetching (bank accounts for Data Center overview)
    if (route.includes("/data-center")) {
      await queryClient.prefetchQuery({
        queryKey: bankKeys.list(),
        queryFn: () => getBankAccountsWithTraining(companyId),
        staleTime: queryOptions.bankAccounts.staleTime,
      });
    }

    if (route.includes("/vendors")) {
      await queryClient.prefetchQuery({
        queryKey: vendorKeys.list(),
        queryFn: () => getVendors(companyId),
        staleTime: queryOptions.vendors.staleTime,
      });
    }

    if (route.includes("/menu") || route.includes("/operations/menu")) {
      await queryClient.prefetchQuery({
        queryKey: menuKeys.items(),
        queryFn: () => getMenuItems(companyId),
        staleTime: queryOptions.menu.staleTime,
      });
    }

    if (route.includes("/company") || route.includes("/settings")) {
      await queryClient.prefetchQuery({
        queryKey: companyKeys.settings(),
        queryFn: () => getCompanySettings(companyId),
        staleTime: queryOptions.company.staleTime,
      });
    }
  } catch (error) {
    // Silently fail - prefetching shouldn't block the UI
    console.debug("Route prefetch error (non-critical):", error);
  }
};
